import { useState } from 'react';
import { readCustomFieldValue } from '../../lib/chartDefinitions';
import { extractLinkedinProfileSlug } from '../../lib/linkedinCsvImport';

const LINKEDIN_CUSTOM_KEYS = ['linkedin_url', 'linkedin', 'linked_url', 'linkedinurl'] as const;
const PHOTO_CUSTOM_KEYS = ['photo_url', 'profile_picture_url', 'headshot', 'photo'] as const;

const SIZE_CLASS = {
  sm: 'h-8 w-8 text-[10px]',
  md: 'h-10 w-10 text-xs',
} as const;

function readFirstCustomField(customData: unknown, keys: readonly string[]): string {
  for (const key of keys) {
    const value = readCustomFieldValue(customData, key);
    if (value) return value;
  }
  return '';
}

/** Best avatar URL: CRM photo custom field, then LinkedIn via unavatar. */
export function resolveSubscriberAvatarSrc(customData: unknown): string | null {
  for (const key of PHOTO_CUSTOM_KEYS) {
    const url = readCustomFieldValue(customData, key);
    if (/^https?:\/\//i.test(url)) return url;
  }

  const linkedinRaw = readFirstCustomField(customData, LINKEDIN_CUSTOM_KEYS);
  const slug = extractLinkedinProfileSlug(linkedinRaw);
  if (slug) {
    return `https://unavatar.io/linkedin.com/in/${encodeURIComponent(slug)}`;
  }

  return null;
}

function subscriberInitials(name?: string | null, email?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase() || '?';
  }
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  const local = (email ?? '').trim().split('@')[0] ?? '';
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  if (local.length === 1) return local.toUpperCase();
  return '?';
}

export default function SubscriberAvatar({
  customData,
  name,
  email,
  size = 'sm',
  className = '',
}: {
  customData?: unknown;
  name?: string | null;
  email?: string | null;
  size?: keyof typeof SIZE_CLASS;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const src = resolveSubscriberAvatarSrc(customData);
  const initials = subscriberInitials(name, email);
  const sizeClass = SIZE_CLASS[size];

  if (!src || failed) {
    return (
      <div
        className={`${sizeClass} rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center font-semibold text-gray-600 dark:text-gray-300 shrink-0 ${className}`}
        aria-hidden
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      width={size === 'md' ? 40 : 32}
      height={size === 'md' ? 40 : 32}
      className={`${sizeClass} rounded-full object-cover shrink-0 ring-1 ring-gray-200 dark:ring-gray-600 bg-gray-100 dark:bg-gray-700 ${className}`}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}
